import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Search, X} from 'lucide-react';
import {useHistory} from '@docusaurus/router';
import Translate, {translate} from '@docusaurus/Translate';
import {useBaseUrlUtils} from '@docusaurus/useBaseUrl';

import {getSearchCatalog, type SearchCatalogItem, type SearchGroup} from './searchCatalog';
import styles from './styles.module.css';

export const OPEN_SEARCH_EVENT = 'mupag:open-search';

const groupOrder: SearchGroup[] = ['guides', 'endpoints', 'events'];
const resultsId = 'docs-command-search-results';

function resultId(index: number): string {
  return `docs-command-search-result-${index}`;
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
}

function groupLabel(group: SearchGroup): string {
  const labels = {
    guides: translate({id: 'search.group.guides', message: 'Guias'}),
    endpoints: translate({id: 'search.group.endpoints', message: 'Endpoints'}),
    events: translate({id: 'search.group.events', message: 'Eventos'}),
  };
  return labels[group];
}

export default function CommandSearch(): React.ReactElement {
  const history = useHistory();
  const {withBaseUrl} = useBaseUrlUtils();
  const catalog = useMemo(() => getSearchCatalog(), []);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const results = useMemo(() => {
    const normalizedQuery = normalize(query.trim());
    if (!normalizedQuery) {
      return catalog;
    }
    return catalog.filter((item) =>
      normalize(`${item.label} ${item.description} ${item.keywords}`).includes(normalizedQuery),
    );
  }, [catalog, query]);

  const groupedResults = useMemo(
    () => groupOrder.map((group) => ({group, items: results.filter((item) => item.group === group)})),
    [results],
  );

  function openSearch() {
    previousFocusRef.current = document.activeElement as HTMLElement;
    setOpen(true);
  }

  function closeSearch() {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
    requestAnimationFrame(() => previousFocusRef.current?.focus());
  }

  function activate(item: SearchCatalogItem) {
    history.push(withBaseUrl(item.to));
    closeSearch();
  }

  useEffect(() => {
    function handleShortcut(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openSearch();
      }
    }
    function handleExternalOpen() {
      openSearch();
    }
    window.addEventListener('keydown', handleShortcut);
    window.addEventListener(OPEN_SEARCH_EVENT, handleExternalOpen);
    return () => {
      window.removeEventListener('keydown', handleShortcut);
      window.removeEventListener(OPEN_SEARCH_EVENT, handleExternalOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open || !results[activeIndex]) {
      return;
    }
    requestAnimationFrame(() => {
      document.getElementById(resultId(activeIndex))?.scrollIntoView({block: 'nearest'});
    });
  }, [activeIndex, open, results]);

  function handleDialogKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSearch();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % Math.max(results.length, 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + Math.max(results.length, 1)) % Math.max(results.length, 1));
      return;
    }
    if (event.key === 'Enter' && results[activeIndex]) {
      event.preventDefault();
      activate(results[activeIndex]);
      return;
    }
    if (event.key === 'Tab' && panelRef.current) {
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>('button, input, [href], [tabindex]:not([tabindex="-1"])'),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  }

  let itemIndex = -1;

  return (
    <div className={styles.root}>
      <button type="button" className={styles.trigger} onClick={openSearch} aria-haspopup="dialog" aria-label={translate({id: 'search.trigger', message: 'Buscar na documentação'})}>
        <Search aria-hidden="true" size={17} />
        <span className={styles.triggerLabel}>
          <Translate id="search.trigger">Buscar na documentação</Translate>
        </span>
        <kbd className={styles.shortcut}>Ctrl K</kbd>
      </button>

      {open && (
        <div className={styles.overlay} role="presentation" onMouseDown={closeSearch}>
          <div
            ref={panelRef}
            className={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="docs-search-title"
            onKeyDown={handleDialogKeyDown}
            onMouseDown={(event) => event.stopPropagation()}>
            <div className={styles.header}>
              <Search aria-hidden="true" size={19} />
              <label id="docs-search-title" className={styles.inputLabel} htmlFor="docs-command-search">
                <Translate id="search.label">Buscar na documentação</Translate>
              </label>
              <input
                ref={inputRef}
                id="docs-command-search"
                className={styles.input}
                type="search"
                role="combobox"
                aria-autocomplete="list"
                aria-controls={resultsId}
                aria-expanded="true"
                aria-activedescendant={results[activeIndex] ? resultId(activeIndex) : undefined}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={translate({id: 'search.placeholder', message: 'PIX, criar cobrança, charge.paid...'})}
                autoComplete="off"
              />
              <button type="button" className={styles.closeButton} onClick={closeSearch} aria-label={translate({id: 'search.close', message: 'Fechar busca'})}>
                <X aria-hidden="true" size={18} />
              </button>
            </div>

            <p className={styles.resultCount} aria-live="polite">
              {translate(
                {id: 'search.resultCount', message: '{count} resultados'},
                {count: results.length},
              )}
            </p>

            <div id={resultsId} className={styles.results} role="listbox" aria-label={translate({id: 'search.results', message: 'Resultados da busca'})}>
              {groupedResults.map(({group, items}) => {
                if (items.length === 0) {
                  return null;
                }
                return (
                  <section className={styles.group} key={group} aria-labelledby={`search-group-${group}`}>
                    <h2 id={`search-group-${group}`} className={styles.groupTitle}>{groupLabel(group)}</h2>
                    {items.map((item) => {
                      itemIndex += 1;
                      const currentIndex = itemIndex;
                      return (
                        <button
                          id={resultId(currentIndex)}
                          type="button"
                          role="option"
                          aria-selected={activeIndex === currentIndex}
                          className={styles.result}
                          data-active={activeIndex === currentIndex}
                          key={`${group}-${item.label}`}
                          onMouseEnter={() => setActiveIndex(currentIndex)}
                          onClick={() => activate(item)}>
                          <span className={styles.resultLabel}>{item.label}</span>
                          <span className={styles.resultDescription}>{item.description}</span>
                        </button>
                      );
                    })}
                  </section>
                );
              })}
              {results.length === 0 && (
                <div className={styles.empty}>
                  <strong><Translate id="search.empty.title">Nenhum resultado encontrado</Translate></strong>
                  <span><Translate id="search.empty.body">Tente buscar por produto, ação ou evento.</Translate></span>
                </div>
              )}
            </div>
            <div className={styles.footer}>
              <span><kbd>↑</kbd><kbd>↓</kbd> <Translate id="search.hint.navigate">navegar</Translate></span>
              <span><kbd>Enter</kbd> <Translate id="search.hint.open">abrir</Translate></span>
              <span><kbd>Esc</kbd> <Translate id="search.hint.close">fechar</Translate></span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
